import { useEffect, useMemo, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";

// Componentes
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs";
import FriendlyPhrase from "../../components/FriendlyPhrase/FriendlyPhrase";

// Contextos
import { useToast } from "../../contexts/ToastContext";
import { useLoader } from "../../contexts/LoaderContext";

// API
import { useTesteService } from "../../services/testeServices";
import { useAlgoritmoTesteService } from "../../services/algoritmoTesteServices";

// Estilos
import styles from "./scoringspecformpage.module.css";

// Icons
import {
  FaPlus, FaTrash, FaCopy, FaDownload, FaArrowUp, FaArrowDown, FaRegCheckCircle, FaChevronDown
} from "react-icons/fa";
import { MdOutlineColorLens } from "react-icons/md";

/* ==================== Constantes / Helpers gerais ==================== */

const DEFAULT_COMBINATION_POLICY = {
  optionMapping: "on",
  tuples: "on",
  exclusions: [],
  // NOVO: modo de atribuição de pontos aos perfis
  profileAssignmentMode: "percent", // "percent" ou "raw"
};

const DEFAULT_PROFILE_COLOR = "#00AFA9";
const DEFAULT_PROFILE_NAMES = ["Executor", "Analítico", "Colaborativo", "Criativo"];

/** Fallback de id (apenas para casos legados sem id no teste) */
const fallbackId = (idx) => `LEGACY_QIDX_${idx}`;

/** IDs locais de perfil (apenas UI) */
const makeProfileId = (name) => `P_${name}_${Math.random().toString(36).slice(2, 8)}`;
const toProfilesObj = (names = []) => names.map(n => ({ id: makeProfileId(n), name: n }));
const fromProfilesObjNames = (arr = []) => arr.map(p => p.name);

/** Normalização de cores (garante #RRGGBB por nome) */
const HEX_RE = /^#([0-9A-Fa-f]{6})$/;
const normalizeHex = (val) =>
  (typeof val === "string" && HEX_RE.test(val.trim())) ? val.trim().toUpperCase() : DEFAULT_PROFILE_COLOR;

function normalizeProfileColors(inputColors, names) {
  const safe = inputColors && typeof inputColors === "object" ? inputColors : {};
  const out = {};
  (names || []).forEach(n => { out[n] = normalizeHex(safe[n]); });
  return out;
}

const safeParseJson = (val, fallback = {}) => {
  if (!val) return fallback;
  if (typeof val === "object") return val;
  try { return JSON.parse(val); } catch { return fallback; }
};

/** ========= NOVO: garante perfis com IDs persistentes ao carregar o algoritmo ========= */
function ensureProfilesWithIdsFromAlg(alg = {}) {
  const raw =
    (Array.isArray(alg.profilesV2) && alg.profilesV2) ||
    (Array.isArray(alg.profilesObj) && alg.profilesObj) ||
    (Array.isArray(alg.profiles) && alg.profiles) ||
    DEFAULT_PROFILE_NAMES;

  let obj;
  if (Array.isArray(raw) && raw.length && typeof raw[0] === "object") {
    obj = raw.map(p => ({ id: p.id || makeProfileId(p.name), name: p.name }));
  } else {
    obj = toProfilesObj(raw);
  }

  const names = obj.map(p => p.name);
  const rawColors =
    alg.profileColors ||
    (alg.profilesMeta && alg.profilesMeta.colors) ||
    {};
  const colors = normalizeProfileColors(rawColors, names);

  return { profilesObj: obj, colors };
}

/** ========= NOVO: normaliza grupos de perfis (tríades) a partir do algoritmo ========= */
function normalizeProfileGroupsFromAlg(alg = {}, profilesObj = []) {
  const raw = Array.isArray(alg.profileGroups) ? alg.profileGroups : [];
  if (!raw.length) return [];
  const profilesById = new Map(profilesObj.map(p => [p.id, p.name]));
  const profileNamesSet = new Set(profilesObj.map(p => p.name));

  return raw.map((g, idx) => {
    const list = Array.isArray(g.profiles) ? g.profiles : [];
    const normProfiles = list
      .map(p => {
        if (profileNamesSet.has(p)) return p;
        const byId = profilesById.get(p);
        return byId || null;
      })
      .filter(Boolean);

    return {
      id: g.id || `PG${idx + 1}`,
      label: g.label || g.name || "",
      profiles: normProfiles,
    };
  });
}

/* ==================== Página ==================== */

function ScoringSpecFormPage() {
  const { idTeste, idAlgoritmo } = useParams();
  const navigate = useNavigate();
  const { showToast } = useToast();
  const { showLoader, hideLoader } = useLoader();

  const { obterTestePorId } = useTesteService();
  const {
    criarAlgoritmo,
    atualizarAlgoritmo,
    obterAlgoritmoPorId,
  } = useAlgoritmoTesteService();

  const [scrollBottom, setScrollBottom] = useState(false);

  // ======= Estados base ======= 
  const [testeNome, setTesteNome] = useState("");
  // [{id,index,enunciado,tipo,opcoes:[{letra,texto}], scale?}]
  const [questions, setQuestions] = useState([]);

  // Metadados do Algoritmo
  const [tituloAlgoritmo, setTituloAlgoritmo] = useState("");
  const [versao, setVersao] = useState(1);
  const minVersaoRef = useRef(1);
  const [status, setStatus] = useState("published");

  // Perfis (UI com IDs) + CORES (por nome)
  const [profilesObj, setProfilesObj] = useState(toProfilesObj(DEFAULT_PROFILE_NAMES));
  const profiles = useMemo(() => fromProfilesObjNames(profilesObj), [profilesObj]);
  const [profileColors, setProfileColors] = useState({});
  const [newProfile, setNewProfile] = useState("");

  // Grupos de perfis
  const [profileGroups, setProfileGroups] = useState([]);
  const [newProfileGroupLabel, setNewProfileGroupLabel] = useState("");

  // Regras por pergunta
  // Para scale, usaremos: { ..., type: "scale", scaleProfiles: [...] }
  const [rules, setRules] = useState([]);

  // Tuplas
  const [tuples, setTuples] = useState([]);
  // Grupos (subtestes)
  const [groups, setGroups] = useState([]);
  // Política de combinação
  const [combinationPolicy, setCombinationPolicy] = useState(DEFAULT_COMBINATION_POLICY);

  const isReadOnly = Boolean(idAlgoritmo && status === "published");

  const breadcrumbLabels = useMemo(() => {
    const base = [{ id: idTeste, label: testeNome || "Teste" }];
    if (idAlgoritmo) base.push({ id: idAlgoritmo, label: tituloAlgoritmo || "Algoritmo" });
    return base;
  }, [idTeste, idAlgoritmo, testeNome, tituloAlgoritmo]);

  // ======= Helpers de acesso ======= 
  const questionById = useMemo(
    () => Object.fromEntries((questions || []).map(q => [q.id, q])),
    [questions]
  );

  const allQuestionOptions = useMemo(
    () => questions.map(q => ({ id: q.id, label: `Q${q.index} — ${q.enunciado}` })),
    [questions]
  );

  const idsForIndexRange = (from, to) =>
    questions.filter(q => q.index >= from && q.index <= to).map(q => q.id);

  const idsFromScope = (scope) => {
    const qBy = scope?.questions?.by || "indexRange";
    if (qBy === "idList") {
      return (scope?.questions?.list || []).filter(Boolean);
    }
    const from = Math.max(1, Number(scope?.questions?.from) || 1);
    const to = Math.min(questions.length, Number(scope?.questions?.to) || questions.length);
    return idsForIndexRange(from, to);
  };

  const lettersForScope = (scope) => {
    const set = new Set();
    const ids = idsFromScope(scope);
    ids.forEach(id => {
      const q = questionById[id];
      (q?.opcoes || []).forEach(op => set.add(op.letra));
    });
    return Array.from(set).sort((a, b) => {
      const na = Number(a);
      const nb = Number(b);
      if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
      return a.localeCompare(b);
    });
  };

  // ======= Carregar teste + (se houver) algoritmo ======= 
  useEffect(() => {
    async function load() {
      if (!idTeste) {
        showToast("Teste não informado.", 3500, "warning");
        navigate("/testes");
        return;
      }
      try {
        showLoader(idAlgoritmo ? "Carregando algoritmo" : "Carregando teste");

        // 1) Carrega teste
        const resTeste = await obterTestePorId(idTeste);
        if (!resTeste?.success) {
          showToast("Erro ao carregar o teste", 4000, "error");
          navigate("/testes");
          return;
        }
        setTesteNome(resTeste.data?.nome || "");

        const estruturaParsed = (() => {
          try { return JSON.parse(resTeste.data.estrutura || "{}"); }
          catch { return {}; }
        })();

        // Normaliza perguntas com ID (closed, priority, open, scale)
        const qs = (estruturaParsed.questions || []).map((q, idx) => {
          const id = (q.id && String(q.id)) || fallbackId(idx + 1);

          let opcoes = [];
          const isScale = q.type === "scale";

          if (q.type === "closed" || q.type === "priority" || isScale) {
            const entries = Object.entries(q.options || {});
            opcoes = entries
              .sort(([a], [b]) => {
                if (isScale) {
                  const na = Number(a);
                  const nb = Number(b);
                  if (Number.isFinite(na) && Number.isFinite(nb)) return na - nb;
                }
                return a.localeCompare(b);
              })
              .map(([letra, texto]) => ({ letra, texto }));
          }

          const scaleMeta = isScale && q.scale && typeof q.scale === "object"
            ? {
                min: Number.isFinite(Number(q.scale.min)) ? Number(q.scale.min) : undefined,
                max: Number.isFinite(Number(q.scale.max)) ? Number(q.scale.max) : undefined,
                labels: q.scale.labels || {}
              }
            : null;

          return {
            id,
            index: idx + 1,
            enunciado: q.question || "",
            tipo: q.type,
            opcoes,
            ...(scaleMeta ? { scale: scaleMeta } : {})
          };
        });
        setQuestions(qs);

        // 2) Se for edição, carrega algoritmo
        if (idAlgoritmo) {
          const resAlg = await obterAlgoritmoPorId(idAlgoritmo);
          if (!resAlg?.success || !resAlg.data) {
            showToast("Algoritmo não encontrado.", 4000, "error");
            navigate(`/algoritmos`);
            return;
          }

          const a = resAlg.data;
          setTituloAlgoritmo(a.titulo || "");
          const vAtual = Number(a.versao || 1);
          setVersao(vAtual);
          minVersaoRef.current = vAtual;
          setStatus(a.status || "published");

          const rawAlg = a.algoritmo ?? a.algoritmo_json ?? a.algoritmoJson ?? null;
          const alg = safeParseJson(rawAlg, {});

          const { profilesObj: loadedProfilesObj, colors: normalizedColors } = ensureProfilesWithIdsFromAlg(alg);
          setProfilesObj(loadedProfilesObj);
          setProfileColors(normalizedColors);

          const loadedProfileGroups = normalizeProfileGroupsFromAlg(alg, loadedProfilesObj);
          setProfileGroups(loadedProfileGroups);

          const alignedRules = alignRulesToQuestions(alg?.questions || [], qs);
          setRules(alignedRules);

          const migratedTuples = (alg.tuples || []).map((t, tIdx) => ({
            id: t.id || `T${tIdx + 1}`,
            label: t.label || "",
            targets: Array.isArray(t.targets) ? t.targets : [],
            members: (t.members || []).map(m => {
              const idRef = remapRefToCurrent(m?.ref, qs);
              return {
                ref: { by: "id", value: idRef ?? qs[0]?.id },
                option: m?.option || (qs[0]?.opcoes?.[0]?.letra ?? "A"),
              };
            }),
            aggregation: t.aggregation || "sum",
            scale: Number(t.scale) || 1,
            requirement: t.requirement || "any",
          }));
          setTuples(migratedTuples);

          const migratedGroups = (alg.groups || []).map((g, gIdx) => {
            const base = {
              id: g.id || `G${gIdx + 1}`,
              label: g.label || "",
              channels: Array.isArray(g.channels) ? g.channels : ["optionMapping"],
              profiles: Array.isArray(g.profiles) ? g.profiles : [],
              normalize: g.normalize || "percent",
              weight: Number(g.weight) || 1,
              missingPolicy: g.missingPolicy || "exclude",
              zeroHandling: g.zeroHandling || "zeros",
            };

            if (g?.scope?.mode === "tuples") {
              return { ...base, scope: { mode: "tuples", tuples: Array.isArray(g?.scope?.tuples) ? g.scope.tuples : [] } };
            }

            let list = [];
            const by = g?.scope?.questions?.by || "indexRange";

            if (by === "idList") {
              list = fixIdList(g?.scope?.questions?.list || [], qs);
            } else if (by === "indexList") {
              const idxList = (g?.scope?.questions?.list || []).map(Number).filter(n => Number.isFinite(n));
              list = idxList
                .map(idx => qs.find(qq => qq.index === idx)?.id)
                .filter(Boolean);
            } else {
              const from = Number(g?.scope?.questions?.from) || 1;
              const to = Number(g?.scope?.questions?.to) || qs.length;
              list = idsForIndexRange(from, to);
            }

            const optionsLetters = g?.scope?.options && g.scope.options.length ? g.scope.options : [];
            return {
              ...base,
              scope: {
                mode: "options",
                questions: { by: "idList", list },
                options: optionsLetters,
              }
            };
          });
          setGroups(migratedGroups);

          // NOVO: garante defaults para algoritmos antigos
          const loadedPolicy = alg.combinationPolicy || {};
          setCombinationPolicy({
            ...DEFAULT_COMBINATION_POLICY,
            ...loadedPolicy,
          });
        } else {
          // 3) Criação
          setProfilesObj(toProfilesObj(DEFAULT_PROFILE_NAMES));
          setProfileColors(prev => normalizeProfileColors(prev, DEFAULT_PROFILE_NAMES));
          setProfileGroups([]);

          setRules(initRulesFromQuestions(qs));
          setTuples([]);
          setGroups([]);
          setCombinationPolicy(DEFAULT_COMBINATION_POLICY);
          setTituloAlgoritmo(resTeste.data?.nome ? `Algoritmo - ${resTeste.data.nome}` : "Algoritmo de Pontuação");
          setVersao(1);
          minVersaoRef.current = 1;
          setStatus("draft");
        }
      } catch (e) {
        console.error(e);
        showToast("Erro ao carregar dados", 4000, "error");
        navigate(`/algoritmos`);
      } finally {
        hideLoader();
      }
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idTeste, idAlgoritmo]);

  // Mantém profileColors sincronizado com os NOMES em profiles
  useEffect(() => {
    setProfileColors(prev => {
      const next = { ...prev };
      let changed = false;

      profiles.forEach(p => {
        if (!next[p]) { next[p] = DEFAULT_PROFILE_COLOR; changed = true; }
      });
      Object.keys(next).forEach(k => {
        if (!profiles.includes(k)) { delete next[k]; changed = true; }
      });

      return changed ? next : prev;
    });
  }, [profiles]);

  /* ==================== Helpers principais ==================== */

  function resolveRefToId(ref, qs) {
    if (!ref) return null;
    if (ref.by === "id") return String(ref.value);
    if (ref.by === "text") {
      const q = qs.find(qq => (qq.enunciado || "") === String(ref.value || ""));
      return q?.id || null;
    }
    if (ref.by === "index") {
      const idx = Number(ref.value);
      const q = qs.find(qq => qq.index === idx);
      return q?.id || null;
    }
    return null;
  }

  function remapRefToCurrent(ref, qs) {
    if (!ref) return null;
    let id = null;

    if (ref.by === "id") {
      const raw = String(ref.value);
      if (qs.some(q => q.id === raw)) return raw;
      const byLegacy = tryLegacyIdxToId(raw, qs);
      if (byLegacy) return byLegacy;
      return null;
    }

    if (ref.by === "text") {
      const q = qs.find(qq => (qq.enunciado || "") === String(ref.value || ""));
      id = q?.id || null;
      return id;
    }

    if (ref.by === "index") {
      const idx = Number(ref.value);
      const q = qs.find(qq => qq.index === idx);
      id = q?.id || null;
      return id;
    }

    return null;
  }

  function fixIdList(list, qs) {
    const ids = [];
    const existing = new Set(qs.map(q => q.id));
    list.forEach(oldId => {
      const raw = String(oldId);
      if (existing.has(raw)) {
        ids.push(raw);
      } else {
        const mapped = tryLegacyIdxToId(raw, qs);
        if (mapped) ids.push(mapped);
      }
    });
    return Array.from(new Set(ids));
  }

  function tryLegacyIdxToId(maybeLegacy, qs) {
    const m = /^LEGACY_QIDX_(\d+)$/.exec(String(maybeLegacy));
    if (m) {
      const idx = Number(m[1]);
      const byIdx = qs.find(qq => qq.index === idx)?.id;
      if (byIdx) return byIdx;
    }
    return null;
  }

  function alignRulesToQuestions(algRules = [], qs = []) {
    const mapByResolvedId = new Map();
    algRules.forEach(r => {
      const resolved = remapRefToCurrent(r?.ref, qs);
      if (resolved) mapByResolvedId.set(resolved, r);
    });

    return qs.map(q => {
      const base = {
        ref: { by: "id", value: q.id },
        type: q.tipo,
        optionToProfiles: Object.fromEntries((q.opcoes || []).map(o => [o.letra, []])),
      };

      const found = mapByResolvedId.get(q.id);
      if (!found) {
        if (q.tipo === "closed") {
          return {
            ...base,
            closedScoring: {
              selectedWeight: 1,
              optionWeights: Object.fromEntries((q.opcoes || []).map(o => [o.letra, 1])),
            }
          };
        }
        if (q.tipo === "priority") {
          const n = (q.opcoes || []).length;
          const positionWeights = Array.from({ length: n }, (_, i) => n - i);
          return { ...base, priorityScoring: { positionWeights } };
        }
        if (q.tipo === "scale") {
          // Apenas vinculação da pergunta com perfis (scaleProfiles)
          return { ...base, scaleProfiles: [] };
        }
        return base;
      }

      const merged = { ...base };

      const otp = {};
      (q.opcoes || []).forEach(o => {
        otp[o.letra] = Array.isArray(found?.optionToProfiles?.[o.letra])
          ? found.optionToProfiles[o.letra]
          : [];
      });
      merged.optionToProfiles = otp;

      if (q.tipo === "closed") {
        const ow = {};
        (q.opcoes || []).forEach(o => {
          const v = Number(found?.closedScoring?.optionWeights?.[o.letra]);
          ow[o.letra] = Number.isFinite(v) ? v : 1;
        });
        merged.closedScoring = {
          selectedWeight: Number(found?.closedScoring?.selectedWeight) || 1,
          optionWeights: ow
        };
      }

      if (q.tipo === "priority") {
        const n = (q.opcoes || []).length;
        const src = Array.isArray(found?.priorityScoring?.positionWeights)
          ? found.priorityScoring.positionWeights
          : [];
        const arr = Array.from({ length: n }, (_, i) => Number(src[i]) || 0);
        merged.priorityScoring = { positionWeights: arr };
      }

      if (q.tipo === "scale") {
        merged.scaleProfiles = Array.isArray(found?.scaleProfiles)
          ? found.scaleProfiles
          : [];
      }

      return merged;
    });
  }

  function initRulesFromQuestions(qs) {
    return qs.map(q => {
      const base = {
        ref: { by: "id", value: q.id },
        type: q.tipo,
        optionToProfiles: Object.fromEntries((q.opcoes || []).map(o => [o.letra, []])),
      };
      if (q.tipo === "closed") {
        return {
          ...base,
          closedScoring: {
            selectedWeight: 1,
            optionWeights: Object.fromEntries((q.opcoes || []).map(o => [o.letra, 1]))
          }
        };
      }
      if (q.tipo === "priority") {
        const n = (q.opcoes || []).length;
        const positionWeights = Array.from({ length: n }, (_, i) => n - i);
        return { ...base, priorityScoring: { positionWeights } };
      }
      if (q.tipo === "scale") {
        // só vinculação pergunta -> perfis
        return { ...base, scaleProfiles: [] };
      }
      return base;
    });
  }

  /* ==================== Ações: Perfis ==================== */

  const addProfile = () => {
    const p = (newProfile || "").trim();
    if (!p) return;
    if (profiles.includes(p)) {
      showToast("Esse perfil já existe.", 2500, "warning");
      return;
    }
    setProfilesObj(prev => [...prev, { id: makeProfileId(p), name: p }]);
    setProfileColors(prev => ({ ...prev, [p]: DEFAULT_PROFILE_COLOR }));
    setNewProfile("");
  };

  const removeProfile = (profileId) => {
    let removedName = null;
    setProfilesObj(prev => {
      const found = prev.find(p => p.id === profileId);
      removedName = found?.name || null;
      return prev.filter(p => p.id !== profileId);
    });
    setProfileColors(prev => {
      if (!removedName) return prev;
      const { [removedName]: _, ...rest } = prev;
      return rest;
    });
    // Limpa mapeamentos nas outras estruturas
    setRules(prev => prev.map(r => ({
      ...r,
      optionToProfiles: Object.fromEntries(
        Object.entries(r.optionToProfiles || {}).map(([k, arr]) => [k, (arr || []).filter(p => p !== removedName)])
      ),
      scaleProfiles: Array.isArray(r.scaleProfiles)
        ? r.scaleProfiles.filter(p => p !== removedName)
        : r.scaleProfiles
    })));
    setTuples(prev => prev.map(t => ({ ...t, targets: (t.targets || []).filter(p => p !== removedName) })));
    setGroups(prev => prev.map(g => ({ ...g, profiles: (g.profiles || []).filter(p => p !== removedName) })));
    setProfileGroups(prev => prev.map(g => ({
      ...g,
      profiles: (g.profiles || []).filter(p => p !== removedName),
    })));
  };

  /* ==================== Ações: Grupos de Perfis ==================== */

  const addProfileGroup = () => {
    const label = (newProfileGroupLabel || "").trim();
    if (!label) return;

    setProfileGroups(prev => {
      if (prev.some(g => (g.label || "").toLowerCase() === label.toLowerCase())) {
        showToast("Esse grupo de perfis já existe.", 2500, "warning");
        return prev;
      }
      return [
        ...prev,
        { id: `PG${prev.length + 1}`, label, profiles: [] }
      ];
    });
    setNewProfileGroupLabel("");
  };

  const removeProfileGroup = (groupId) => {
    setProfileGroups(prev => prev.filter(g => g.id !== groupId));
  };

  const updateProfileGroup = (groupId, patch) => {
    setProfileGroups(prev =>
      prev.map(g => (g.id === groupId ? { ...g, ...patch } : g))
    );
  };

  /* ==================== Ações: Rules (por pergunta) ==================== */

  const setOptionProfiles = (ruleIdx, optionLetter, values) => {
    setRules(prev => {
      const next = [...prev];
      const r = { ...next[ruleIdx] };
      r.optionToProfiles = { ...(r.optionToProfiles || {}), [optionLetter]: values };
      next[ruleIdx] = r;
      return next;
    });
  };

  const setClosedWeight = (ruleIdx, optionLetter, value) => {
    setRules(prev => {
      const next = [...prev];
      const r = { ...next[ruleIdx] };
      const cw = { ...(r.closedScoring || { selectedWeight: 1, optionWeights: {} }) };
      cw.optionWeights = { ...(cw.optionWeights || {}), [optionLetter]: Number(value) || 0 };
      r.closedScoring = cw;
      next[ruleIdx] = r;
      return next;
    });
  };

  const setClosedSelectedWeight = (ruleIdx, value) => {
    setRules(prev => {
      const next = [...prev];
      const r = { ...next[ruleIdx] };
      const cw = { ...(r.closedScoring || { selectedWeight: 1, optionWeights: {} }) };
      cw.selectedWeight = Number(value) || 0;
      r.closedScoring = cw;
      next[ruleIdx] = r;
      return next;
    });
  };

  const setPriorityPositionWeight = (ruleIdx, posIdx, value) => {
    setRules(prev => {
      const next = [...prev];
      const r = { ...next[ruleIdx] };
      const ps = { ...(r.priorityScoring || { positionWeights: [] }) };
      const arr = [...(ps.positionWeights || [])];
      arr[posIdx] = Number(value) || 0;
      ps.positionWeights = arr;
      r.priorityScoring = ps;
      next[ruleIdx] = r;
      return next;
    });
  };

  // NOVO: vincular pergunta de escala a perfis (question-level)
  const setScaleProfiles = (ruleIdx, values) => {
    setRules(prev => {
      const next = [...prev];
      const r = { ...next[ruleIdx] };
      r.scaleProfiles = values;
      next[ruleIdx] = r;
      return next;
    });
  };

  /* ==================== Tuplas ==================== */

  const addTuple = () => {
    setTuples(prev => ([
      ...prev,
      { id: `T${prev.length + 1}`, label: "", targets: [], members: [], aggregation: "sum", scale: 1, requirement: "any" }
    ]));
  };

  const removeTuple = (idx) => {
    setTuples(prev => prev.filter((_, i) => i !== idx));
    setGroups(prev => prev.map(g => {
      if (g?.scope?.mode !== "tuples") return g;
      const keep = (g.scope.tuples || []).filter(tid => tid !== `T${idx + 1}`);
      return { ...g, scope: { ...g.scope, tuples: keep } };
    }));
  };

  const addTupleMember = (tIdx) => {
    setTuples(prev => {
      const ns = [...prev];
      const t = { ...ns[tIdx] };
      const firstQ = questions[0];
      const firstId = firstQ?.id;
      const firstLetter = firstQ?.opcoes?.[0]?.letra ?? "A";
      t.members = [...(t.members || []), { ref: { by: "id", value: firstId }, option: firstLetter }];
      ns[tIdx] = t;
      return ns;
    });
  };

  const updateTuple = (tIdx, patch) => {
    setTuples(prev => {
      const ns = [...prev];
      ns[tIdx] = { ...ns[tIdx], ...patch };
      return ns;
    });
  };

  const updateTupleMember = (tIdx, mIdx, patch) => {
    setTuples(prev => {
      const ns = [...prev];
      const t = { ...ns[tIdx] };
      const ms = [...(t.members || [])];
      ms[mIdx] = { ...ms[mIdx], ...patch };
      t.members = ms;
      ns[tIdx] = t;
      return ns;
    });
  };

  const removeTupleMember = (tIdx, mIdx) => {
    setTuples(prev => {
      const ns = [...prev];
      const t = { ...ns[tIdx] };
      t.members = (t.members || []).filter((_, i) => i !== mIdx);
      ns[tIdx] = t;
      return ns;
    });
  };

  /* ==================== Grupos ==================== */

  const addGroup = () => {
    setGroups(prev => ([
      ...prev,
      {
        id: `G${prev.length + 1}`,
        label: "",
        scope: {
          mode: "options",
          questions: { by: "indexRange", from: 1, to: Math.max(1, questions.length) },
          options: ["A", "B", "C", "D"]
        },
        channels: ["optionMapping"],
        profiles: [],
        normalize: "percent",
        weight: 1,
        missingPolicy: "exclude",
        zeroHandling: "zeros"
      }
    ]));
  };

  const updateGroup = (gIdx, patch) => {
    setGroups(prev => {
      const ns = [...prev];
      ns[gIdx] = { ...ns[gIdx], ...patch };
      return ns;
    });
  };

  const removeGroup = (gIdx) => setGroups(prev => prev.filter((_, i) => i !== gIdx));

  /* ==================== Spec (JSON) ==================== */

  const spec = useMemo(
    () => generateSpec({
      profiles,
      profilesObj,
      profileColors,
      profileGroups,
      rules,
      tuples,
      groups,
      combinationPolicy,
      testeNome,
      idTeste,
      tituloAlgoritmo,
      questions
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [profiles, profilesObj, profileColors, profileGroups, rules, tuples, groups, combinationPolicy, testeNome, idTeste, tituloAlgoritmo, questions]
  );

  function generateSpec({
    profiles,
    profilesObj,
    profileColors,
    profileGroups,
    rules,
    tuples,
    groups,
    combinationPolicy,
    testeNome,
    idTeste,
    tituloAlgoritmo,
    questions
  }) {
    const questionRules = rules.filter(r =>
      r?.type === "closed" || r?.type === "priority" || r?.type === "scale"
    );

    const normalizedGroups = (groups || []).map(g => {
      const base = {
        id: g.id,
        label: g.label,
        channels: g.channels && g.channels.length ? g.channels : ["optionMapping"],
        profiles: g.profiles && g.profiles.length ? g.profiles : undefined,
        normalize: g.normalize || "percent",
        weight: Number(g.weight) || 1,
        missingPolicy: g.missingPolicy || "exclude",
        zeroHandling: g.zeroHandling || "zeros",
      };

      if (g?.scope?.mode === "tuples") {
        return { ...base, scope: { mode: "tuples", tuples: g?.scope?.tuples || [] } };
      }

      const byUI = g?.scope?.questions?.by || "indexRange";
      let list = [];
      if (byUI === "idList") {
        list = fixIdList(g?.scope?.questions?.list || [], questions);
      } else {
        const from = Number(g?.scope?.questions?.from) || 1;
        const to = Number(g?.scope?.questions?.to) || (questions?.length || from);
        list = questions.filter(q => q.index >= from && q.index <= to).map(q => q.id);
      }

      return {
        ...base,
        scope: {
          mode: "options",
          questions: { by: "idList", list },
          options: g?.scope?.options && g.scope.options.length ? g.scope.options : undefined,
        },
      };
    });

    return {
      meta: {
        titulo: tituloAlgoritmo || "Algoritmo de Pontuação",
        testeId: idTeste,
        testeNome,
        criadoEm: new Date().toISOString()
      },
      profiles,
      profilesV2: (profilesObj || []).map(p => ({ id: p.id, name: p.name })),
      profilesMeta: { colors: profileColors || {} },
      profileGroups: (profileGroups || []).map((g, idx) => ({
        id: g.id || `PG${idx + 1}`,
        label: g.label || "",
        profiles: (g.profiles || []).filter(p => profiles.includes(p)),
      })),
      questions: questionRules.map(r => {
        const base = {
          ref: { by: "id", value: resolveRefToId(r.ref, questions) || r.ref?.value },
          type: r.type,
          optionToProfiles: r.optionToProfiles || {}
        };
        if (r.type === "closed") return { ...base, closedScoring: r.closedScoring };
        if (r.type === "priority") return { ...base, priorityScoring: r.priorityScoring };
        if (r.type === "scale") {
          return {
            ...base,
            scaleProfiles: Array.isArray(r.scaleProfiles) ? r.scaleProfiles : []
          };
        }
        return base;
      }),
      tuples: (tuples || []).map(t => ({
        id: t.id,
        label: t.label,
        targets: t.targets || [],
        members: (t.members || []).map(m => ({
          ref: { by: "id", value: remapRefToCurrent(m.ref, questions) || questions[0]?.id },
          option: m.option
        })),
        aggregation: t.aggregation || "sum",
        scale: Number(t.scale) || 1,
        requirement: t.requirement || "any",
      })),
      groups: normalizedGroups,
      combinationPolicy: combinationPolicy || DEFAULT_COMBINATION_POLICY,
    };
  }

  /* ==================== Validação ==================== */

  const validateSpec = () => {
    const problems = [];
    if (!tituloAlgoritmo.trim()) problems.push("Informe um título para o algoritmo.");
    if (!profiles.length) problems.push("Adicione ao menos um perfil.");

    rules.forEach((r, idx) => {
      const qId = resolveRefToId(r.ref, questions);
      const q = qId ? questionById[qId] : null;

      if (!q) {
        problems.push(`Regra #${idx + 1}: referência de pergunta inválida.`);
        return;
      }

      if (r.type === "priority") {
        const nOpts = q?.opcoes?.length || 0;
        const nW = r?.priorityScoring?.positionWeights?.length || 0;
        if (nOpts !== nW) problems.push(`Q${q.index}: a lista de Pesos por Posição deve ter ${nOpts} valores.`);
      }

      if (r.type === "closed") {
        const letters = (q?.opcoes || []).map(o => o.letra);
        const ow = r?.closedScoring?.optionWeights || {};
        for (const L of letters) {
          if (typeof ow[L] === "undefined") {
            problems.push(`Q${q.index}: defina o Peso por Alternativa para '${L}'.`);
          }
        }
      }

      // Mapeamentos de perfis:
      let allMappings = [];
      if (r.type === "scale") {
        allMappings = Array.isArray(r.scaleProfiles) ? r.scaleProfiles : [];
        if (allMappings.length === 0) {
          problems.push(`Q${q.index}: associe a pergunta de escala a pelo menos um perfil.`);
        }
      } else {
        allMappings = Object.values(r.optionToProfiles || {}).flat();
      }

      for (const p of allMappings) {
        if (!profiles.includes(p)) {
          problems.push(`Q${q.index}: o perfil '${p}' não existe no catálogo.`);
        }
      }
    });

    tuples.forEach((t) => {
      (t.members || []).forEach((m) => {
        const qId = remapRefToCurrent(m?.ref, questions);
        const q = qId ? questionById[qId] : null;
        if (!q) problems.push(`Tupla ${t.id}: referência de pergunta inválida em um membro.`);
        if (q && !q.opcoes.some(o => o.letra === m.option)) problems.push(`Tupla ${t.id}: alternativa '${m.option}' não existe em Q${q.index}.`);
      });
      (t.targets || []).forEach(p => {
        if (!profiles.includes(p)) problems.push(`Tupla ${t.id}: perfil de destino '${p}' não existe.`);
      });
    });

    groups.forEach((g) => {
      if (!g.id) problems.push("Grupo sem ID.");

      if (g?.scope?.mode === "tuples") {
        (g?.scope?.tuples || []).forEach(tid => {
          if (!tuples.some(t => t.id === tid)) problems.push(`Grupo ${g.id}: a tupla '${tid}' não existe.`);
        });
      } else {
        const by = g?.scope?.questions?.by || "indexRange";
        if (by === "idList") {
          const list = fixIdList(g?.scope?.questions?.list || [], questions);
          if (list.length === 0) problems.push(`Grupo ${g.id}: escolha ao menos uma questão.`);
          list.forEach(qid => {
            if (!questionById[qid]) problems.push(`Grupo ${g.id}: pergunta '${qid}' é inválida para este teste.`);
          });
        } else {
          const from = Number(g?.scope?.questions?.from) || 1;
          const to = Number(g?.scope?.questions?.to) || questions.length;
          if (from < 1 || to > questions.length || from > to) {
            problems.push(`Grupo ${g.id}: faixa de questões inválida (${from}-${to}).`);
          }
        }
      }

      (g?.profiles || []).forEach(p => {
        if (!profiles.includes(p)) problems.push(`Grupo ${g.id}: o perfil '${p}' não existe.`);
      });
    });

    profileGroups.forEach((g) => {
      (g.profiles || []).forEach(p => {
        if (!profiles.includes(p)) {
          problems.push(`Grupo de perfis '${g.label || g.id}': o perfil '${p}' não existe.`);
        }
      });
    });

    if (problems.length) {
      problems.slice(0, 10).forEach(msg => showToast(msg, 3500, "warning"));
      if (problems.length > 10) showToast(`+${problems.length - 10} outras inconsistências…`, 3000, "warning");
      return false;
    }
    showToast("Configuração parece consistente!", 2000, "success");
    return true;
  };

  /* ==================== Export helpers ==================== */

  const copyToClipboard = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(spec, null, 2));
      showToast("JSON copiado!", 2000, "success");
    } catch {
      showToast("Não foi possível copiar.", 2500, "error");
    }
  };

  const downloadJson = () => {
    const blob = new Blob([JSON.stringify(spec, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `algoritmo_${(tituloAlgoritmo || "pontuacao").replace(/\s+/g, "-")}.json`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  /* ==================== Salvar (Create/Update) ==================== */

  const salvarAlgoritmo = async () => {
    if (!validateSpec()) return;

    try {
      showLoader(idAlgoritmo ? "Atualizando algoritmo" : "Criando algoritmo");

      if (!idAlgoritmo) {
        const payload = {
          testeId: idTeste,
          titulo: tituloAlgoritmo.trim(),
          algoritmo: spec,
          versao: Number(versao) || 1,
          status: "draft",
          publishExclusive: false
        };
        const res = await criarAlgoritmo(payload);
        if (res?.success) {
          showToast("Algoritmo criado como rascunho!", 3000, "success");
          if (res.id) navigate(`/algoritmos`);
        } else {
          showToast(res?.message || "Falha ao criar algoritmo", 3500, "error");
        }
      } else {
        const payload = {
          id: idAlgoritmo,
          titulo: tituloAlgoritmo.trim(),
          algoritmo: spec,
          versao: Number(versao) || 1,
        };
        const res = await atualizarAlgoritmo(payload);
        if (res?.success) {
          showToast("Algoritmo atualizado com sucesso!", 3000, "success");
          navigate(`/algoritmos`);
        } else {
          showToast(res?.message || "Falha ao atualizar algoritmo", 3500, "error");
        }
      }
    } catch (e) {
      console.error(e);
      showToast("Erro ao salvar algoritmo", 3500, "error");
    } finally {
      hideLoader();
    }
  };

  /* ==================== Criar nova versão (rascunho) ==================== */

  const criarNovaVersao = async () => {
    try {
      const nextVersao = (Number(versao) || 1) + 1;
      showLoader("Criando nova versão (rascunho)");

      const res = await criarAlgoritmo({
        testeId: idTeste,
        titulo: tituloAlgoritmo?.trim() ? `${tituloAlgoritmo.trim()}` : "Algoritmo de Pontuação",
        algoritmo: spec,
        versao: nextVersao,
        status: "draft",
        publishExclusive: false,
      });

      if (res?.success && res?.id) {
        showToast("Nova versão criada como rascunho!", 3000, "success");
        navigate(`/algoritmos`);
      } else {
        showToast(res?.message || "Não foi possível criar a nova versão", 3500, "error");
      }
    } catch (e) {
      console.error(e);
      showToast("Erro ao criar nova versão", 3500, "error");
    } finally {
      hideLoader();
    }
  };

  /* ==================== Scroll bottom btn ==================== */

  useEffect(() => {
    const handleScroll = () => {
      const nearBottom = window.innerHeight + window.scrollY >= document.body.offsetHeight - 100;
      setScrollBottom(nearBottom);
    };
    handleScroll();
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  /* ==================== Render ==================== */

  return (
    <div className={styles.ScoringSpecFormPage}>
      <div className={styles.containerTop}>
        <Breadcrumbs idLabels={breadcrumbLabels} />
        <h1 className={styles.title}>{idAlgoritmo ? "Editar Algoritmo" : "Criar Algoritmo"}</h1>
        <FriendlyPhrase page="scoringSpecForm" />
      </div>

      {/* ===== Dados do Algoritmo ===== */}
      <CollapsibleSection title="Dados do Algoritmo" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        {isReadOnly && (
          <div style={{ marginBottom: 12 }}>
            <p className={styles.mutedNote} style={{ color: '#C62828', fontWeight: 600, marginBottom: 8 }}>
              Esta versão está publicada e é imutável. Para alterar, crie uma nova versão.
            </p>
            <button
              type="button"
              className={styles.addQuestionBtn}
              onClick={criarNovaVersao}
            >
              <FaPlus /> Criar nova versão (rascunho)
            </button>
          </div>
        )}

        <div className={styles.dataSection}>
          <label className={styles.dataInput}>
            Título
            <input
              type="text"
              className={styles.input}
              placeholder="Ex.: Algoritmo de Pontuação v1"
              value={tituloAlgoritmo}
              onChange={(e) => setTituloAlgoritmo(e.target.value)}
              disabled={isReadOnly}
            />
          </label>

          <label className={styles.dataInput}>
            Versão
            <input
              type="number"
              min={minVersaoRef.current}
              className={styles.input}
              value={versao}
              onChange={(e) => {
                const val = Number(e.target.value);
                if (Number.isFinite(val)) {
                  setVersao(Math.max(minVersaoRef.current, Math.floor(val)));
                }
              }}
              onBlur={() => {
                setVersao(v => Math.max(minVersaoRef.current, Math.floor(Number(v) || minVersaoRef.current)));
              }}
              style={{ width: 120 }}
              disabled={isReadOnly}
            />
          </label>

          <div className={styles.dataInput}>
            <span style={{ display: "block", fontWeight: 600, marginBottom: 6 }}>Status</span>
            <span
              className={styles.typeBadge}
              style={{
                background: status === "published" ? "#2E7D32" : status === "draft" ? "#616161" : "#6A1B9A"
              }}
            >
              {status === "published" ? "Publicado" : status === "draft" ? "Rascunho" : "Arquivado"}
            </span>
          </div>
        </div>
      </CollapsibleSection>

      {/* ===== Perfis + CORES ===== */}
      <CollapsibleSection title="Perfis" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>
          Cadastre os perfis e defina uma cor para cada um. As pílulas que mostram perfis usarão essas cores.
        </p>

        <div className={styles.divContent}>
          <input
            type="text"
            placeholder="Novo Perfil"
            className={styles.input}
            value={newProfile}
            onChange={e => setNewProfile(e.target.value)}
            disabled={isReadOnly}
          />
          <button type="button" className={styles.addQuestionBtn} onClick={addProfile} disabled={isReadOnly}>
            <FaPlus /> Adicionar
          </button>
        </div>

        <div className={styles.profileList}>
          {profilesObj.map(p => (
            <div key={p.id} className={styles.profileItem}>
              <span className={styles.profileName}>{p.name}</span>

              <label className={styles.colorIconBtn} title={`Cor do perfil ${p.name}`}>
                <MdOutlineColorLens
                  className={styles.colorIcon}
                  style={{ color: profileColors[p.name] || DEFAULT_PROFILE_COLOR }}
                />
                <input
                  type="color"
                  className={styles.colorInputHidden}
                  value={profileColors[p.name] || DEFAULT_PROFILE_COLOR}
                  onChange={e => setProfileColors(prev => ({ ...prev, [p.name]: e.target.value }))}
                  aria-label={`Selecionar cor para o perfil ${p.name}`}
                  disabled={isReadOnly}
                />
              </label>

              <span
                className={styles.profileSwatch}
                style={{ backgroundColor: profileColors[p.name] || DEFAULT_PROFILE_COLOR }}
                aria-hidden
              />
              <button
                type="button"
                className={styles.removerCorBtn}
                onClick={() => removeProfile(p.id)}
                disabled={isReadOnly}
              >
                Remover
              </button>
            </div>
          ))}
          {profilesObj.length === 0 && (
            <p className={styles.mutedNote}>Nenhum perfil cadastrado.</p>
          )}
        </div>
      </CollapsibleSection>

      {/* ===== Grupos de perfis ===== */}
      <CollapsibleSection
        title="Grupos de Perfis"
        sectionClass={styles.generalArea}
        titleClass={styles.sectionTitle}
      >
        <p className={styles.mutedNote}>
          Agrupe perfis em conjuntos maiores (ex.: Qualidade de Vida, Saúde &amp; Energia, etc.).
        </p>

        <div className={styles.divContent}>
          <input
            type="text"
            placeholder="Nome do novo grupo (ex.: Tríade Profissional)"
            className={styles.input}
            value={newProfileGroupLabel}
            onChange={e => setNewProfileGroupLabel(e.target.value)}
            disabled={isReadOnly}
          />
          <button
            type="button"
            className={styles.addQuestionBtn}
            onClick={addProfileGroup}
            disabled={isReadOnly}
          >
            <FaPlus /> Adicionar
          </button>
        </div>

        <div className={styles.profileList}>
          {profileGroups.map(g => (
            <div key={g.id} className={styles.profileItem} style={{ flexDirection: "column", alignItems: "stretch" }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <strong>{g.id}</strong>
                <input
                  type="text"
                  className={styles.input}
                  placeholder="Nome do grupo"
                  value={g.label || ""}
                  onChange={e => updateProfileGroup(g.id, { label: e.target.value })}
                  disabled={isReadOnly}
                  style={{ minWidth: 260 }}
                />
                <button
                  type="button"
                  className={styles.removerCorBtn}
                  onClick={() => removeProfileGroup(g.id)}
                  disabled={isReadOnly}
                >
                  <FaTrash style={{ marginRight: 4 }} /> Remover Grupo
                </button>
              </div>

              <div style={{ marginTop: 8 }}>
                <p className={styles.mutedNote} style={{ marginBottom: 4 }}>Perfis deste grupo</p>
                <MultiSelect
                  values={g.profiles || []}
                  options={profiles}
                  onChange={(vals) => updateProfileGroup(g.id, { profiles: vals })}
                  colorMap={profileColors}
                  disabled={isReadOnly}
                />
              </div>
            </div>
          ))}

          {profileGroups.length === 0 && (
            <p className={styles.mutedNote}>Nenhum grupo de perfis cadastrado.</p>
          )}
        </div>
      </CollapsibleSection>

      {/* ===== Regras por pergunta ===== */}
      <CollapsibleSection title="Regras por Pergunta" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>
          Defina como cada alternativa contribui para os perfis. Em <b>Fechada</b>, só a escolhida pontua; em <b>Prioridade</b>, todas pontuam conforme a posição; em <b>Escala</b>, o valor escolhido (0–10, por exemplo) é usado diretamente como pontuação, e você apenas escolhe <b>para quais perfis</b> essa pergunta credita pontos.
        </p>

        {questions.map((q, i) => {
          const r = rules[i] || {};
          const isClosed = q.tipo === "closed";
          const isPriority = q.tipo === "priority";
          const isScale = q.tipo === "scale";

          return (
            <div
              key={q.id}
              className={styles.perguntaCard}
              style={{
                borderLeftColor: isPriority
                  ? "#CD1870"
                  : isClosed
                  ? "#855FDF"
                  : isScale
                  ? "#041838"
                  : "#e0e0e0"
              }}
            >
              <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", gap: 12 }}>
                <h3 style={{ margin: 0 }}>Q{q.index} · {q.enunciado}</h3>
                <span
                  className={styles.typeBadge}
                  style={{
                    background: isPriority
                      ? "#CD1870"
                      : isClosed
                      ? "#855FDF"
                      : isScale
                      ? "#041838"
                      : "#9e9e9e"
                  }}
                >
                  {isPriority ? "PRIORIDADE" : isClosed ? "FECHADA" : isScale ? "ESCALA" : "ABERTA"}
                </span>
              </div>

              {isClosed && (
                <div>
                  <p className={styles.mutedNote}>
                    <b>Pergunta Fechada:</b> somente a alternativa escolhida recebe o <b>Peso ao selecionar</b> (padrão 1). Você pode personalizar o <b>Peso por Alternativa</b>.
                  </p>

                  <div style={{ display: "flex", gap: 16, flexWrap: "wrap", margin: "6px 0 12px" }}>
                    <label> Peso ao selecionar:{" "}
                      <input
                        className={styles.input}
                        type="number"
                        min={0}
                        step={1}
                        value={r?.closedScoring?.selectedWeight ?? 1}
                        onChange={e => setClosedSelectedWeight(i, e.target.value)}
                        disabled={isReadOnly}
                      />
                    </label>
                  </div>

                  <div className={styles.tableWrapper}>
                    <table className={styles.tableLike}>
                      <thead>
                        <tr>
                          <th>Letra</th>
                          <th>Opção</th>
                          <th>Peso por Alternativa</th>
                          <th>Perfis que recebem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(q.opcoes || []).map(op => (
                          <tr key={op.letra}>
                            <td><span className={styles.badgeLetra}>{op.letra}</span></td>
                            <td>{op.texto}</td>
                            <td>
                              <input
                                className={styles.input}
                                type="number"
                                min={0}
                                step={1}
                                value={r?.closedScoring?.optionWeights?.[op.letra] ?? 1}
                                onChange={e => setClosedWeight(i, op.letra, e.target.value)}
                                disabled={isReadOnly}
                              />
                            </td>
                            <td>
                              <MultiSelect
                                values={r?.optionToProfiles?.[op.letra] || []}
                                options={profiles}
                                onChange={vals => setOptionProfiles(i, op.letra, vals)}
                                colorMap={profileColors}
                                disabled={isReadOnly}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {isPriority && (
                <div>
                  <p className={styles.mutedNote}>
                    <b>Pergunta de Prioridade:</b> todas as alternativas recebem pontos pela <b>posição</b>. Ajuste os <b>Pesos por Posição</b>.
                  </p>

                  <div className={styles.gridWeights}>
                    {(q.opcoes || []).map((op, idx) => (
                      <label key={op.letra}>
                        Posição {idx + 1}:{" "}
                        <input
                          className={styles.input}
                          type="number"
                          step={1}
                          value={r?.priorityScoring?.positionWeights?.[idx] ?? 0}
                          onChange={e => setPriorityPositionWeight(i, idx, e.target.value)}
                          disabled={isReadOnly}
                        />
                      </label>
                    ))}
                  </div>

                  <div className={styles.tableWrapper}>
                    <table className={styles.tableLike}>
                      <thead>
                        <tr>
                          <th>Letra</th>
                          <th>Opção</th>
                          <th style={{ width: 320 }}>Perfis que recebem</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(q.opcoes || []).map(op => (
                          <tr key={op.letra}>
                            <td><span className={styles.badgeLetra}>{op.letra}</span></td>
                            <td>{op.texto}</td>
                            <td>
                              <MultiSelect
                                values={r?.optionToProfiles?.[op.letra] || []}
                                options={profiles}
                                onChange={vals => setOptionProfiles(i, op.letra, vals)}
                                colorMap={profileColors}
                                disabled={isReadOnly}
                              />
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {isScale && (
                <div>
                  <p className={styles.mutedNote}>
                    <b>Pergunta de Escala:</b> o participante escolhe um valor (por exemplo, de 0 a 10).
                    O valor escolhido é usado diretamente como pontuação (0 vale 0, 10 vale 10).
                    Aqui você apenas define <b>quais perfis recebem esses pontos</b>.
                  </p>

                  {q.scale && (
                    <p className={styles.mutedNote} style={{ marginTop: 4 }}>
                      Escala configurada: {q.scale.min ?? "?"} até {q.scale.max ?? "?"}.
                    </p>
                  )}

                  <div style={{ marginTop: 8, marginBottom: 16 }}>
                    <h4 style={{ margin: "4px 0" }}>Perfis que recebem os pontos desta pergunta</h4>
                    <MultiSelect
                      values={r.scaleProfiles || []}
                      options={profiles}
                      onChange={(vals) => setScaleProfiles(i, vals)}
                      colorMap={profileColors}
                      disabled={isReadOnly}
                    />
                  </div>

                  <div className={styles.tableWrapper}>
                    <table className={styles.tableLike}>
                      <thead>
                        <tr>
                          <th>Valor</th>
                          <th>Rótulo</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(q.opcoes || []).map(op => (
                          <tr key={op.letra}>
                            <td><span className={styles.badgeLetra}>{op.letra}</span></td>
                            <td>{op.texto || "-"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}

              {q.tipo === "open" && (
                <div className={styles.mutedNote}>Pergunta aberta — não participa da pontuação.</div>
              )}
            </div>
          );
        })}
      </CollapsibleSection>

      {/* ===== Tuplas ===== */}
      <CollapsibleSection title="Tuplas" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>
          Some pesos de alternativas específicas de perguntas diferentes e credite o total a um ou mais perfis.
        </p>

        {(tuples || []).length === 0 ? (
          <p className={styles.mutedNote}>Nenhuma tupla adicionada.</p>
        ): (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button type="button" className={styles.addQuestionBtn} onClick={addTuple} disabled={isReadOnly}>
              <FaPlus /> Nova Tupla
            </button>
          </div>
        )}

        {(tuples || []).map((t, tIdx) => (
          <div key={t.id} className={styles.perguntaCard} style={{ borderLeftColor: "#00C5B9" }}>
            <div className={styles.tupUpper}>
              <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
                <strong>{t.id}</strong>
                <input
                  type="text"
                  placeholder="Descrição da Tupla"
                  className={styles.input}
                  value={t.label || ""}
                  onChange={e => updateTuple(tIdx, { label: e.target.value })}
                  style={{ minWidth: 240 }}
                  disabled={isReadOnly}
                />
                <label> Agregação:{" "}
                  <select
                    className={styles.selectInput}
                    value={t.aggregation || "sum"}
                    onChange={e => updateTuple(tIdx, { aggregation: e.target.value })}
                    disabled={isReadOnly}
                  >
                    <option value="sum">Somar</option>
                    <option value="min">Mínimo</option>
                    <option value="max">Máximo</option>
                    <option value="avg">Média</option>
                  </select>
                </label>
                <label> Escala:{" "}
                  <input
                    className={styles.input}
                    type="number"
                    step={0.1}
                    value={t.scale ?? 1}
                    onChange={e => updateTuple(tIdx, { scale: Number(e.target.value) })}
                    style={{ width: 120 }}
                    disabled={isReadOnly}
                  />
                </label>
                <label> Obrigatoriedade:{" "}
                  <select
                    className={styles.selectInput}
                    value={t.requirement || "any"}
                    onChange={e => updateTuple(tIdx, { requirement: e.target.value })}
                    disabled={isReadOnly}
                  >
                    <option value="any">Qualquer membro</option>
                    <option value="all">Todos os membros</option>
                  </select>
                </label>
              </div>
              <button type="button" className={styles.removerBtn} onClick={() => removeTuple(tIdx)} disabled={isReadOnly}>
                <FaTrash /> Remover Tupla
              </button>
            </div>

            <div className={styles.profSelect}>
              <h4>Perfis de Destino</h4>
              <MultiSelect
                values={t.targets || []}
                options={profiles}
                onChange={(vals) => updateTuple(tIdx, { targets: vals })}
                colorMap={profileColors}
                disabled={isReadOnly}
              />
            </div>

            <div style={{ marginTop: 12 }}>
              <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                <h4 style={{ margin: 0 }}>Membros da Tupla</h4>
                <button type="button" className={styles.addQuestionBtn} onClick={() => addTupleMember(tIdx)} disabled={isReadOnly}>
                  <FaPlus /> Adicionar
                </button>
              </div>

              {(t.members || []).length === 0 && (
                <p className={styles.mutedNote} style={{ marginTop: 6 }}>Nenhum membro. Adicione (Pergunta, Alternativa).</p>
              )}

              {(t.members || []).map((m, mIdx) => {
                const currentQId = remapRefToCurrent(m?.ref, questions) || questions[0]?.id;
                const q = questionById[currentQId];
                return (
                  <div key={mIdx} className={styles.opcaoRow} style={{ alignItems: "center", marginTop: 18 }}>
                    <label>Pergunta:{" "}
                      <select
                        className={styles.selectInput}
                        value={currentQId}
                        onChange={e => {
                          const newId = e.target.value;
                          const newQ = questionById[newId];
                          const firstLetter = newQ?.opcoes?.[0]?.letra ?? "A";
                          updateTupleMember(tIdx, mIdx, { ref: { by: "id", value: newId }, option: firstLetter });
                        }}
                        disabled={isReadOnly}
                      >
                        {questions.map(qo => (
                          <option key={qo.id} value={qo.id}>Q{qo.index} — {qo.enunciado}</option>
                        ))}
                      </select>
                    </label>

                    <label style={{ marginLeft: 12 }}>Alternativa:{" "}
                      <select
                        className={styles.selectInput}
                        value={m.option || (q?.opcoes?.[0]?.letra ?? "A")}
                        onChange={e => updateTupleMember(tIdx, mIdx, { option: e.target.value })}
                        disabled={isReadOnly}
                      >
                        {(q?.opcoes || []).map(op => (
                          <option key={op.letra} value={op.letra}>{op.letra}</option>
                        ))}
                      </select>
                    </label>

                    <button type="button" className={styles.removerOpBtn} title="Remover membro" onClick={() => removeTupleMember(tIdx, mIdx)} disabled={isReadOnly}>
                      ×
                    </button>
                  </div>
                );
              })}
            </div>
          </div>
        ))}

        <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <button type="button" className={styles.addQuestionBtn} onClick={addTuple} disabled={isReadOnly}>
            <FaPlus /> Nova Tupla
          </button>
        </div>
      </CollapsibleSection>

      {/* ===== Grupos (subtestes) ===== */}
      <CollapsibleSection title="Grupos (Subtestes)" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>
          Agrupe trechos do algoritmo por <b>Opções</b> (faixa de questões + alternativas) ou por <b>Tuplas</b>.
        </p>

        {(groups || []).length === 0 ? (
          <p className={styles.mutedNote} style={{ marginTop: 8 }}>
            Nenhum grupo adicionado.
          </p>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
            <button type="button" className={styles.addQuestionBtn} onClick={addGroup} disabled={isReadOnly}>
              <FaPlus /> Novo Grupo
            </button>
          </div>
        )}

        {(groups || []).map((g, gIdx) => {
          const tupleIds = (tuples || []).map(t => t.id);

          return (
            <div key={g.id} className={styles.perguntaCard} style={{ borderLeftColor: "#041838" }}>
              <div className={styles.grpUpper}>
                <div className={styles.grpUpperLeft}>
                  <strong className={styles.groupTitle}>{g.id}</strong>

                  <input
                    className={styles.input}
                    type="text"
                    placeholder="Descrição do grupo"
                    value={g.label || ""}
                    onChange={e => updateGroup(gIdx, { label: e.target.value })}
                    style={{ minWidth: 260 }}
                    disabled={isReadOnly}
                  />

                  <label> Modo:{" "}
                    <select
                      className={styles.selectInput}
                      value={g?.scope?.mode || "options"}
                      onChange={e =>
                        updateGroup(
                          gIdx,
                          {
                            scope: e.target.value === "tuples"
                              ? { mode: "tuples", tuples: [] }
                              : { mode: "options", questions: { by: "indexRange", from: 1, to: Math.max(1, questions.length) }, options: ["A", "B", "C", "D"] }
                          }
                        )
                      }
                      disabled={isReadOnly}
                    >
                      <option value="options">Opções</option>
                      <option value="tuples">Tuplas</option>
                    </select>
                  </label>

                  <label> Formato do Resultado:{" "}
                    <select
                      className={styles.selectInput}
                      value={g.normalize || "percent"}
                      onChange={e => updateGroup(gIdx, { normalize: e.target.value })}
                      disabled={isReadOnly}
                    >
                      <option value="percent">Percentual (%)</option>
                    </select>
                  </label>

                  <label> Peso do Grupo:{" "}
                    <input
                      className={styles.input}
                      type="number"
                      step={0.1}
                      value={g.weight ?? 1}
                      onChange={e => updateGroup(gIdx, { weight: Number(e.target.value) })}
                      style={{ width: 120 }}
                      disabled={isReadOnly}
                    />
                  </label>

                  <label> Ausências:{" "}
                    <select
                      className={styles.selectInput}
                      value={g.missingPolicy || "exclude"}
                      onChange={e => updateGroup(gIdx, { missingPolicy: e.target.value })}
                      disabled={isReadOnly}
                    >
                      <option value="exclude">Excluir ausências</option>
                      <option value="penalize">Penalizar ausências</option>
                    </select>
                  </label>

                  <label> Sem Dados:{" "}
                    <select
                      className={styles.selectInput}
                      value={g.zeroHandling || "zeros"}
                      onChange={e => updateGroup(gIdx, { zeroHandling: e.target.value })}
                      disabled={isReadOnly}
                    >
                      <option value="zeros">Zeros</option>
                      <option value="equalSplit">Dividir igualmente</option>
                      <option value="undefined">Indefinido</option>
                    </select>
                  </label>
                </div>

                <button type="button" className={styles.removerBtn} onClick={() => removeGroup(gIdx)} disabled={isReadOnly}>
                  <FaTrash style={{ marginRight: 6 }} /> Remover Grupo
                </button>
              </div>

              {/* Scope */}
              {g?.scope?.mode === "options" ? (
                <div style={{ marginTop: 10 }}>
                  <h4 style={{ margin: "4px 0" }}>Escopo por Opções</h4>

                  {/* Tipo de seleção */}
                  <div className={styles.row} style={{ marginBottom: 20 }}>
                    <label>Tipo de Seleção:{" "}
                      <select
                        className={styles.selectInput}
                        value={g?.scope?.questions?.by || "indexRange"}
                        onChange={e => {
                          const by = e.target.value;
                          updateGroup(gIdx, {
                            scope: {
                              ...g.scope,
                              questions:
                                by === "idList"
                                  ? { by: "idList", list: [] }
                                  : { by: "indexRange", from: 1, to: Math.max(1, questions.length) },
                            }
                          });
                        }}
                        disabled={isReadOnly}
                      >
                        <option value="indexRange">Faixa (de X até Y)</option>
                        <option value="idList">Lista por ID</option>
                      </select>
                    </label>
                  </div>

                  {/* Se for FAIXA */}
                  {(g?.scope?.questions?.by || "indexRange") === "indexRange" && (
                    <>
                      <div className={styles.row}>
                        <label>Da Questão:{" "}
                          <input
                            className={styles.input}
                            type="number"
                            min={1}
                            max={questions.length}
                            value={g?.scope?.questions?.from ?? 1}
                            onChange={e =>
                              updateGroup(gIdx, {
                                scope: {
                                  ...g.scope,
                                  questions: {
                                    by: "indexRange",
                                    from: Number(e.target.value),
                                    to: g?.scope?.questions?.to ?? questions.length
                                  },
                                  options: g?.scope?.options
                                }
                              })
                            }
                            style={{ width: 120 }}
                            disabled={isReadOnly}
                          />
                        </label>
                        <label>Até a Questão:{" "}
                          <input
                            className={styles.input}
                            type="number"
                            min={1}
                            max={questions.length}
                            value={g?.scope?.questions?.to ?? questions.length}
                            onChange={e =>
                              updateGroup(gIdx, {
                                scope: {
                                  ...g.scope,
                                  questions: {
                                    by: "indexRange",
                                    from: g?.scope?.questions?.from ?? 1,
                                    to: Number(e.target.value)
                                  },
                                  options: g?.scope?.options
                                }
                              })
                            }
                            style={{ width: 120 }}
                            disabled={isReadOnly}
                          />
                        </label>
                      </div>

                      {/* Letras válidas */}
                      <div style={{ marginTop: 8 }}>
                        <h4 style={{ margin: "4px 0" }}>Alternativas Consideradas</h4>
                        <p className={styles.mutedNote}>Selecione quais letras de alternativas (A, B, C, … ou valores de escala) contar no intervalo acima.</p>
                        <MultiSelect
                          values={g?.scope?.options || []}
                          options={lettersForScope(g.scope)}
                          onChange={(vals) => updateGroup(gIdx, { scope: { ...g.scope, options: vals } })}
                          disabled={isReadOnly}
                        />
                      </div>
                    </>
                  )}

                  {/* Se for LISTA por ID */}
                  {(g?.scope?.questions?.by || "indexRange") === "idList" && (
                    <>
                      <div className={styles.row}>
                        <label>Questões Selecionadas:</label>
                        <MultiSelectQuestionsMulti
                          selectedIds={(g?.scope?.questions?.list || [])}
                          options={allQuestionOptions}
                          onChange={(ids) =>
                            updateGroup(gIdx, {
                              scope: {
                                ...g.scope,
                                questions: { by: "idList", list: ids },
                              }
                            })
                          }
                        />
                      </div>

                      {/* Letras válidas */}
                      <div style={{ marginTop: 8 }}>
                        <h4 style={{ margin: "4px 0" }}>Alternativas Consideradas</h4>
                        <p className={styles.mutedNote}>Selecione quais letras de alternativas (A, B, C, … ou valores de escala) contar nas questões escolhidas.</p>
                        <MultiSelect
                          values={g?.scope?.options || []}
                          options={lettersForScope(g.scope)}
                          onChange={(vals) => updateGroup(gIdx, { scope: { ...g.scope, options: vals } })}
                          disabled={isReadOnly}
                        />
                      </div>
                    </>
                  )}
                </div>
              ) : (
                <div style={{ marginTop: 10 }}>
                  <h4 style={{ margin: "4px 0" }}>Escopo por Tuplas</h4>
                  <p className={styles.mutedNote}>Escolha as tuplas que compõem este grupo.</p>
                  <MultiSelectStringsMulti
                    selected={(g?.scope?.tuples || [])}
                    options={tupleIds}
                    label="Selecionar tuplas…"
                    onChange={(vals) => updateGroup(gIdx, { scope: { ...g.scope, mode: "tuples", tuples: vals } })}
                  />
                </div>
              )}

              <div style={{ marginTop: 10 }}>
                <h4 style={{ margin: "4px 0" }}>Perfis do Grupo (opcional)</h4>
                <p className={styles.mutedNote}>Se vazio, o grupo considera todos os perfis cadastrados.</p>
                <MultiSelect
                  values={g?.profiles || []}
                  options={profiles}
                  onChange={(vals) => updateGroup(gIdx, { profiles: vals })}
                  colorMap={profileColors}
                  disabled={isReadOnly}
                />
              </div>

              <div style={{ marginTop: 10 }}>
                <h4 style={{ margin: "4px 0" }}>Canais Considerados</h4>
                <div style={{ display: "flex", gap: 24, flexWrap: "wrap", justifyContent: "center", marginTop: 15 }}>
                  {(["optionMapping", "tuples"]).map(ch => (
                    <label key={ch} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <input
                        type="checkbox"
                        checked={(g.channels || []).includes(ch)}
                        onChange={e => {
                          const has = (g.channels || []).includes(ch);
                          const channels = has
                            ? (g.channels || []).filter(c => c !== ch)
                            : [ ...(g.channels || []), ch ];
                          updateGroup(gIdx, { channels });
                        }}
                        disabled={isReadOnly}
                      /> {ch === "optionMapping" ? "Opções" : "Tuplas"}
                    </label>
                  ))}
                </div>
              </div>
            </div>
          );
        })}

        <div style={{ display: "flex", justifyContent: "center", marginTop: 12 }}>
          <button type="button" className={styles.addQuestionBtn} onClick={addGroup} disabled={isReadOnly}>
            <FaPlus /> Novo Grupo
          </button>
        </div>
      </CollapsibleSection>

      {/* ===== Política de combinação ===== */}
      <CollapsibleSection title="Política de Combinação" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>
          Ative/desative como os pontos chegam aos perfis: <b>Opções</b> (direto das alternativas, incluindo escala) e/ou <b>Tuplas</b>.
        </p>
        <div style={{ display: "flex", gap: 24, flexWrap: "wrap", alignItems: "center" }}>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={combinationPolicy.optionMapping !== "off"}
              onChange={e => setCombinationPolicy(p => ({ ...p, optionMapping: e.target.checked ? "on" : "off" }))}
              disabled={isReadOnly}
            /> Opções
          </label>
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            <input
              type="checkbox"
              checked={combinationPolicy.tuples !== "off"}
              onChange={e => setCombinationPolicy(p => ({ ...p, tuples: e.target.checked ? "on" : "off" }))}
              disabled={isReadOnly}
            /> Tuplas
          </label>

          {/* NOVO: Select para modo de atribuição aos perfis */}
          <label style={{ display: "flex", alignItems: "center", gap: 6 }}>
            Modo de atribuição aos perfis:
            <select
              className={styles.selectInput}
              value={combinationPolicy.profileAssignmentMode || "percent"}
              onChange={e =>
                setCombinationPolicy(prev => ({
                  ...prev,
                  profileAssignmentMode: e.target.value,
                }))
              }
              disabled={isReadOnly}
            >
              <option value="percent">Percentual (%)</option>
              <option value="raw">Valor bruto</option>
            </select>
          </label>
        </div>
      </CollapsibleSection>

      {/* ===== Pré-visualização / Exportação ===== */}
      <CollapsibleSection title="Pré-visualização e Exportação" sectionClass={styles.generalArea} titleClass={styles.sectionTitle}>
        <p className={styles.mutedNote}>Confira o JSON gerado. Você pode validá-lo, copiar ou baixar o arquivo e salvar diretamente.</p>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
          <button type="button" className={styles.addQuestionBtn} onClick={validateSpec}><FaRegCheckCircle /> Validar</button>
          <button type="button" className={styles.addQuestionBtn} onClick={copyToClipboard}><FaCopy /> Copiar JSON</button>
          <button type="button" className={styles.addQuestionBtn} onClick={downloadJson}><FaDownload /> Baixar JSON</button>
        </div>
        <pre>{JSON.stringify(spec, null, 2)}</pre>
      </CollapsibleSection>

      {/* ===== Botão Final ===== */}
      <section className={styles.submitArea} style={{ marginTop: 16, width: "100%", maxWidth: "72rem" }}>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <button type="button" className={styles.submitButton} onClick={salvarAlgoritmo} disabled={isReadOnly}>
            {idAlgoritmo ? "Salvar Alterações" : "Criar Algoritmo"}
          </button>
        </div>
      </section>

      <button
        type="button"
        className={styles.scrollToBottomBtn}
        onClick={() =>
          scrollBottom
            ? window.scrollTo({ top: 0, behavior: 'smooth' })
            : window.scrollTo({ top: document.body.scrollHeight, behavior: 'smooth' })
        }
        title={scrollBottom ? 'Ir para o topo' : 'Ir para o final'}
      >
        {scrollBottom ? <FaArrowUp /> : <FaArrowDown />}
      </button>
    </div>
  );
}

export default ScoringSpecFormPage;

/* =================== Componentes auxiliares =================== */

function CollapsibleSection({ title, children, defaultOpen = true, sectionClass, titleClass }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <section className={sectionClass}>
      <div
        onClick={() => setOpen(o => !o)}
        role="button"
        aria-expanded={open}
        className={styles.sectionClickable}
      >
        <h2 className={titleClass}>{title}</h2>

        <FaChevronDown
          aria-hidden
          style={{
            transform: open ? "rotate(0deg)" : "rotate(-90deg)",
            transition: "transform .2s ease"
          }}
        />
      </div>

      {open && <div>{children}</div>}
    </section>
  );
}

/** MultiSelect simples (um a um) */
function MultiSelect({ values, options, onChange, colorMap, disabled }) {
  const [input, setInput] = useState("");
  const selected = new Set(values || []);
  const available = (options || []).filter(o => !selected.has(o));

  const add = (val) => {
    if (!val) return;
    const next = Array.from(new Set([...(values || []), val]));
    onChange(next);
    setInput("");
  };
  const remove = (val) => onChange((values || []).filter(v => v !== val));

  return (
    <div className={styles.inputPills}>
      <div className={styles.produtoPills}>
        {(values || []).map(v => (
          <div
            key={v}
            className={styles.produtoPill}
            style={{ backgroundColor: colorMap?.[v] || undefined }}
            title={v}
          >
            {v}
            {!disabled && <span className={styles.removerPill} onClick={() => remove(v)}>×</span>}
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "center"}}>
        <select
          value={input}
          onChange={e => setInput(e.target.value)}
          className={styles.selectInput}
          disabled={disabled}
        >
          <option value="" disabled>Selecionar</option>
          {available.map(o => <option key={o} value={o}>{o}</option>)}
        </select>
        <button type="button" className={styles.addQuestionBtn} onClick={() => add(input)} disabled={disabled}>
          <FaPlus /> Adicionar
        </button>
      </div>
    </div>
  );
}

/** Multi seleção de QUESTÕES (id + label) com select múltiplo antes de adicionar */
function MultiSelectQuestionsMulti({ selectedIds = [], options = [], onChange }) {
  const [pending, setPending] = useState([]); // ids ainda não aplicados

  const selectedSet = new Set(selectedIds);
  const available = options.filter(o => !selectedSet.has(o.id));

  const addMany = () => {
    if (!pending.length) return;
    const merged = Array.from(new Set([...(selectedIds || []), ...pending]));
    onChange(merged);
    setPending([]);
  };
  const remove = (id) => onChange((selectedIds || []).filter(x => x !== id));

  return (
    <div className={styles.inputPills}>
      <div className={styles.produtoPills}>
        {(selectedIds || []).map(id => {
          const opt = options.find(o => o.id === id);
          const label = opt ? opt.label : id;
          return (
            <div key={id} className={styles.produtoPill}>
              {label}
              <span className={styles.removerPill} onClick={() => remove(id)}>×</span>
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
        <select
          className={styles.selectInput}
          value={pending}
          onChange={e => {
            const opts = Array.from(e.target.selectedOptions).map(o => o.value);
            setPending(opts);
          }}
          multiple
          size={6}
          style={{ minWidth: 320 }}
        >
          {available.map(o => (
            <option key={o.id} value={o.id}>{o.label}</option>
          ))}
        </select>
        <button type="button" className={styles.addQuestionBtn} onClick={addMany} style={{ alignSelf: "center" }}>
          <FaPlus /> Adicionar Selecionadas
        </button>
      </div>
    </div>
  );
}

/** Multi seleção para STRINGS (tuplas) com select múltiplo antes de adicionar */
function MultiSelectStringsMulti({ selected = [], options = [], onChange, label = "Selecionar…" }) {
  const [pending, setPending] = useState([]);

  const selectedSet = new Set(selected);
  const available = (options || []).filter(o => !selectedSet.has(o));

  const addMany = () => {
    if (!pending.length) return;
    const merged = Array.from(new Set([...(selected || []), ...pending]));
    onChange(merged);
    setPending([]);
  };
  const remove = (val) => onChange((selected || []).filter(x => x !== val));

  return (
    <div className={styles.inputPills}>
      <div className={styles.produtoPills}>
        {(selected || []).map(val => (
          <div key={val} className={styles.produtoPill}>
            {val}
            <span className={styles.removerPill} onClick={() => remove(val)}>×</span>
          </div>
        ))}
      </div>

      <div style={{ display: "flex", gap: 8, alignItems: "stretch", flexWrap: "wrap" }}>
        <select
          className={styles.selectInput}
          value={pending}
          onChange={e => {
            const opts = Array.from(e.target.selectedOptions).map(o => o.value);
            setPending(opts);
          }}
          multiple
          size={6}
          style={{ minWidth: 280 }}
          aria-label={label}
        >
          {available.map(o => (
            <option key={o} value={o}>{o}</option>
          ))}
        </select>
        <button type="button" className={styles.addQuestionBtn} onClick={addMany} style={{ alignSelf: "center" }}>
          <FaPlus /> Adicionar Selecionadas
        </button>
      </div>
    </div>
  );
}
